// The page-level states every page shares (consistency-pass spec §2): one
// centred spinner for a first load, and a message — always in
// describeError's words, with Try again — when it fails. A table uses its
// own skeleton rows and takes `tableError` for its error slot and
// `TABLE_LABELS` for its retry button's word.

import { Button, type DataTableLabels, Spinner } from "@pej/kore/components";
import { type ReactNode, useState } from "react";
import { describeError } from "@/api/describe-error";

/** Every DataTable's labels: its retry button says Try again, as PageError does (kore's default is "Retry"). */
export const TABLE_LABELS: DataTableLabels = { retry: "Try again" };

export function PageLoading() {
    return (
        <div data-testid="page-loading" className="flex h-full min-h-40 items-center justify-center">
            <Spinner />
        </div>
    );
}

export interface PageMessageProps {
    title: string;
    message: string;
    action?: ReactNode;
    /** 1 when the message is all the page shows (it is the page's heading);
     *  2 under a page's own h1; 3 inside a card under its h2. */
    level?: 1 | 2 | 3;
    /** Inside a card: no page-sized height or padding. */
    compact?: boolean;
}

export function PageMessage({ title, message, action, level = 2, compact = false }: PageMessageProps) {
    const Heading = `h${level}` as const;
    return (
        <div
            className={
                compact
                    ? "flex flex-col items-center gap-2 py-2 text-center"
                    : "flex h-full min-h-40 flex-col items-center justify-center gap-2 p-8 text-center"
            }
        >
            <Heading className="text-base font-semibold">{title}</Heading>
            <p className="max-w-md text-sm text-kore-text-muted">{message}</p>
            {action}
        </div>
    );
}

export interface PageErrorProps extends Pick<PageMessageProps, "level" | "compact"> {
    error: unknown;
    onRetry: () => void;
    /** A retry is running (useRetry): the button says so and waits. */
    retrying?: boolean;
}

export function PageError({ error, onRetry, retrying = false, level, compact }: PageErrorProps) {
    const { title, message } = describeError(error);
    return (
        <PageMessage
            title={title}
            message={message}
            level={level}
            compact={compact}
            action={
                <Button type="button" variant="outline" size="sm" disabled={retrying} onClick={onRetry}>
                    {retrying ? "Trying again…" : "Try again"}
                </Button>
            }
        />
    );
}

/** What a page needs from a query to show its error and retry it. */
export interface RetryableQuery {
    isError: boolean;
    error: unknown;
    refetch: () => Promise<unknown>;
}

/** Try again for a query: `failed` while it has failed or a retry runs, and
 *  PageError's props. TanStack puts a query that never loaded back to
 *  pending while it refetches, which would swap the error for the page's
 *  spinner; this keeps the error on screen, saying "Trying again…", until
 *  the retry settles. Check `failed` before the loading state. */
export function useRetry(query: RetryableQuery): {
    failed: boolean;
    props: Pick<PageErrorProps, "error" | "onRetry" | "retrying">;
} {
    // The error being retried, held while the query is pending again.
    const [held, setHeld] = useState<{ error: unknown } | null>(null);
    const onRetry = () => {
        setHeld({ error: query.error });
        void query.refetch().finally(() => setHeld(null));
    };
    return {
        failed: query.isError || held !== null,
        props: { error: query.error ?? held?.error, onRetry, retrying: held !== null },
    };
}

/** An address nothing answers: the root's and the workspace's notFoundComponent. */
export function NotFound() {
    return <PageMessage level={1} title="Page not found" message="There's nothing at this address." />;
}

/** A DataTable's error slot in describeError's words. */
export function tableError(
    error: unknown,
    onRetry: () => void,
): { title: string; detail: string; onRetry: () => void } {
    const { title, message } = describeError(error);
    return { title, detail: message, onRetry };
}
